import { UseQueryResult } from "@tanstack/react-query";
import { decamelizeKeys } from "humps";
import { forEach, omit } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useOrderConfigForm } from "PFCore/hooks/queries/saas_admin/config_forms/use_order_config_form";
import { useOrderConfig } from "PFCore/hooks/queries/saas_admin/order_config/use_order_config";
import { useOrderConfigUpdate } from "PFCore/hooks/queries/saas_admin/order_config/use_order_config_update";
import { Id } from "PFTypes";
import { ConfigTemplateType } from "PFTypes/config_template";
import { OrderConfig, OrderConfigTemplate } from "PFTypes/order_config";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export type UseOrderTabReturn = {
  target: ConfigTemplateType | null;
  setTarget: Dispatch<SetStateAction<ConfigTemplateType | null>>;
  config: UseQueryResult<OrderConfig>;
  template: UseQueryResult<OrderConfigTemplate>;
  handleSubmit: (payload: OrderConfig) => void;
  handleError: () => void;
  isSubmitting: boolean;
};

export const useOrderTab = (accountId: Id, enabled: boolean): UseOrderTabReturn => {
  const { t } = useTranslation("saasAdmin");
  const { getFormattedErrors } = useResponseErrors();
  const growl = useGrowl();

  const [target, setTarget] = useState(null);

  const { mutate, isLoading: isSubmitting } = useOrderConfigUpdate(accountId, target || "");

  // we have to decamelize keys results because we match keys with values later
  const orderConfig = useOrderConfig(accountId, target!, {
    enabled: enabled && !!target,
    select: (data) => decamelizeKeys(data) as OrderConfig
  });

  const orderConfigTemplate = useOrderConfigForm(accountId, {
    enabled,
    select: (data) => decamelizeKeys(data) as OrderConfigTemplate
  });

  const sanitizePayload = useCallback((formValues): OrderConfig => {
    // find selected value
    const value = omit(
      formValues.options.find(({ selected }) => selected),
      "selected"
    );
    // remove "selected" property from options
    const options = formValues.options.map((option) => omit(option, "selected"));
    const parsedFormValues = { ...formValues, options, ...(value ? { value } : {}) };
    return parsedFormValues;
  }, []);

  const handleOrderConfigUpdate = useCallback(
    (formValues: OrderConfig) => {
      const payload = sanitizePayload(formValues);
      mutate(payload, {
        onSuccess: () =>
          growl({
            message: t("filtersConfig.success")
          }),
        onError: ({ response }) => {
          const errors = getFormattedErrors(response.data);
          forEach(errors, (message, fieldName) => {
            growl({
              message: `${fieldName}:${message}`,
              kind: "error"
            });
          });
        }
      });
    },
    [getFormattedErrors, mutate, growl, t, sanitizePayload]
  );

  const handleError = () =>
    growl({
      message: t("errors.notifyGrowl"),
      kind: "error"
    });

  return {
    target,
    setTarget,
    config: orderConfig,
    template: orderConfigTemplate,
    handleSubmit: handleOrderConfigUpdate,
    handleError,
    isSubmitting
  };
};
